/**
 * Thin data-fetching hooks over the typed `api` client.
 *
 * Because the client's response middleware THROWS a typed `ApiError` on any
 * non-2xx, these hooks model state as {data | error | loading} where `error`
 * is always an `ApiError` (or a generic Error for network failures). Screens
 * render `<ProblemAlert error={error} />` and switch on `error.code`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, isApiError } from "./client";

export { api, ApiError, isApiError } from "./client";

export type ApiState<T> = {
  data: T | null;
  error: ApiError | Error | null;
  loading: boolean;
  /** Re-run the fetcher (e.g. after an adjudication). */
  reload: () => void;
  /** Re-run the fetcher in the background: keeps the current data on screen
   * (no `loading` flip) and swaps it when the new answer lands. */
  refresh: () => void;
};

function toError(err: unknown): ApiError | Error {
  if (isApiError(err)) return err;
  if (err instanceof Error) return err;
  return new Error(String(err));
}

/**
 * Runs an async fetcher on mount and whenever `deps` change. The fetcher
 * typically awaits `api.GET(...)` and returns `res.data`.
 *
 *   const { data, error, loading } = useApiQuery(
 *     async () => {
 *       const res = await api.GET("/rounds/{round_id}/report", {
 *         params: { path: { round_id: roundId } },
 *       });
 *       return res.data!;
 *     },
 *     [roundId],
 *   );
 */
export function useApiQuery<T>(
  fetcher: () => Promise<T>,
  deps: readonly unknown[],
): ApiState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const silentRef = useRef(false);

  const reload = useCallback(() => {
    silentRef.current = false;
    setNonce((n) => n + 1);
  }, []);
  const refresh = useCallback(() => {
    silentRef.current = true;
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    let active = true;
    const silent = silentRef.current;
    silentRef.current = false;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    fetcher()
      .then((result) => {
        if (active) {
          setData(result);
          setError(null);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        // A failed BACKGROUND refresh keeps what is on screen (data or the
        // previous error) rather than flipping the view to an error.
        if (active && !silent) {
          setError(toError(err));
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload, refresh };
}

/**
 * Imperative mutation helper for POSTs (adjudication, sign, process). Returns a
 * `run` callback plus {loading,error} state; the caller awaits `run(args)`.
 *
 *   const { run, loading, error } = useApiMutation(async (body: AdjudicationRequest) => {
 *     const res = await api.POST(
 *       "/rounds/{round_id}/expedientes/{expediente_id}/adjudication",
 *       { params: { path: { round_id, expediente_id } }, body },
 *     );
 *     return res.data!;
 *   });
 */
export function useApiMutation<TArgs extends unknown[], TResult>(
  mutator: (...args: TArgs) => Promise<TResult>,
): {
  run: (...args: TArgs) => Promise<TResult | undefined>;
  loading: boolean;
  error: ApiError | Error | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);

  const run = useCallback(
    async (...args: TArgs): Promise<TResult | undefined> => {
      setLoading(true);
      setError(null);
      try {
        return await mutator(...args);
      } catch (err: unknown) {
        setError(toError(err));
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { run, loading, error };
}
